
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import api from "../api/api";

import Swal from "sweetalert2";

import {
    Activity,
    ArrowDown,
    ArrowDownUp,
    ArrowLeft,
    ArrowRight,
    Banknote,
    Building2,
    CalendarDays,
    Check,
    CheckCircle2,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    CircleDollarSign,
    ClipboardList,
    Clock3,
    CreditCard,
    Download,
    Eye,
    FilePlus2,
    Filter,
    GraduationCap,
    LoaderCircle,
    MoreHorizontal,
    Pencil,
    RefreshCw,
    Search,
    ShieldCheck,
    Trash2,
    UserRound,
    Users,
    Wallet,
    X,
    XCircle,
} from "lucide-react";

import "./OfflineEntranceApplicants.css";

const ENDPOINT = "/api/entrance-applications/offline-entrance-applicants/";

const PAGE_SIZES = [10, 20, 50, 100];

const PAYMENT_METHODS = [
    { value: "CASH", label: "Cash" },
    { value: "MOBILE_MONEY", label: "Mobile Money" },
    { value: "ORANGE_MONEY", label: "Orange Money" },
    {
        value: "LONESTAR_MOBILE_MONEY",
        label: "Lonestar Mobile Money",
    },
    { value: "BANK", label: "Bank Transfer" },
];

const BANKS = [
    {
        value: "LBDI",
        label: "Liberian Bank for Development & Investment (LBDI)",
    },
    { value: "ECOBANK", label: "Ecobank Liberia" },
    { value: "IBL", label: "International Bank Liberia (IBL)" },
    { value: "BLOOM", label: "Bloom Bank Africa Liberia" },
    {
        value: "SAPELLE",
        label: "Sapelle International Bank Liberia",
    },
    { value: "UBA", label: "United Bank for Africa (UBA)" },
    { value: "ACCESS", label: "AccessBank Liberia" },
    { value: "GTBANK", label: "Guaranty Trust Bank (GTBank)" },
    { value: "AFRILAND", label: "Afriland First Bank Liberia" },
    { value: "GLOBAL_BANK", label: "Global Bank Liberia" },
];

const RESULT_STATUSES = [
    { value: "PASSED", label: "Passed" },
    { value: "FAILED", label: "Failed" },
];

const EMPTY_FORM = {
    full_name: "",
    status: "FAILED",
    amount_paid: "0.00",
    payment_method: "",
    bank_name: "",
};

const moneyFormatter = new Intl.NumberFormat("en-LR", {
    style: "currency",
    currency: "LRD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const dateFormatter = new Intl.DateTimeFormat("en-LR", {
    dateStyle: "medium",
    timeStyle: "short",
});

const normalizeText = (value) =>
    String(value ?? "").trim();

const formatMoney = (value) => {
    const amount = Number(value);

    return moneyFormatter.format(
        Number.isFinite(amount) ? amount : 0
    );
};

const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "—";
    }

    return dateFormatter.format(date);
};

const getPaymentLabel = (value) =>
    PAYMENT_METHODS.find((item) => item.value === value)?.label ||
    value ||
    "Not specified";

const getBankLabel = (value) =>
    BANKS.find((item) => item.value === value)?.label ||
    value ||
    "—";

const getErrorMessage = (error) => {
    const data = error?.response?.data;

    if (typeof data === "string") {
        return data;
    }

    if (data?.detail) {
        return data.detail;
    }

    if (data?.message) {
        return data.message;
    }

    if (data && typeof data === "object") {
        return Object.entries(data)
            .map(([field, messages]) => {
                const text = Array.isArray(messages)
                    ? messages.join(" ")
                    : typeof messages === "string"
                      ? messages
                      : JSON.stringify(messages);

                return `${field.replaceAll("_", " ")}: ${text}`;
            })
            .join("\n");
    }

    if (error?.response?.status === 401) {
        return "Your session has expired. Please sign in again.";
    }

    if (error?.response?.status === 403) {
        return "You do not have permission to perform this operation.";
    }

    if (error?.response?.status === 404) {
        return "The requested applicant record could not be found.";
    }

    if (error?.response?.status === 405) {
        return (
            "This operation is not enabled by the backend API. " +
            "Enable the required ViewSet action before using it."
        );
    }

    return error?.message || "An unexpected error occurred.";
};

const showAlert = ({
    icon = "info",
    title = "",
    text = "",
    confirmButtonText = "OK",
}) =>
    Swal.fire({
        icon,
        title,
        text,
        confirmButtonText,
        confirmButtonColor: "#062A4B",
        color: "#172033",
        background: "#ffffff",
        buttonsStyling: true,
        customClass: {
            popup: "offline-applicant-swal",
            confirmButton: "offline-applicant-swal-confirm",
        },
    });

const confirmAction = ({
    title,
    text,
    confirmButtonText = "Continue",
    icon = "warning",
}) =>
    Swal.fire({
        icon,
        title,
        text,
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText: "Cancel",
        confirmButtonColor: "#062A4B",
        cancelButtonColor: "#64748b",
        reverseButtons: true,
        focusCancel: true,
        customClass: {
            popup: "offline-applicant-swal",
        },
    });

function getPageData(response) {
    const data = response?.data;

    if (Array.isArray(data)) {
        return {
            results: data,
            count: data.length,
            next: null,
            previous: null,
        };
    }

    if (Array.isArray(data?.results)) {
        return {
            results: data.results,
            count: Number(data.count ?? data.results.length),
            next: data.next ?? null,
            previous: data.previous ?? null,
        };
    }

    return {
        results: [],
        count: 0,
        next: null,
        previous: null,
    };
}

function getPageNumberFromUrl(url) {
    if (!url) return null;

    try {
        const parsed = new URL(url, window.location.origin);
        const page = Number(parsed.searchParams.get("page"));

        return Number.isInteger(page) && page > 0
            ? page
            : null;
    } catch {
        return null;
    }
}

function validateForm(form) {
    const errors = {};

    const fullName = normalizeText(form.full_name);
    const amount = normalizeText(form.amount_paid);
    const paymentMethod = normalizeText(form.payment_method);
    const bankName = normalizeText(form.bank_name);

    if (fullName.length < 2) {
        errors.full_name =
            "Enter the applicant's full name using at least two characters.";
    } else if (fullName.length > 255) {
        errors.full_name =
            "The full name cannot exceed 255 characters.";
    } else if (!/\p{L}/u.test(fullName)) {
        errors.full_name =
            "The full name must contain at least one letter.";
    }

    if (amount === "") {
        errors.amount_paid = "Enter the amount paid.";
    } else if (!/^\d+(\.\d{1,2})?$/.test(amount)) {
        errors.amount_paid =
            "Enter a valid non-negative amount with up to two decimal places.";
    } else if (!Number.isFinite(Number(amount))) {
        errors.amount_paid = "Enter a valid amount.";
    } else if (Number(amount) < 0) {
        errors.amount_paid = "Amount paid cannot be negative.";
    } else if (amount.replace(".", "").length > 12) {
        errors.amount_paid =
            "The amount exceeds the allowed maximum.";
    }

    if (!RESULT_STATUSES.some((item) => item.value === form.status)) {
        errors.status = "Select a valid examination result.";
    }

    if (
        !PAYMENT_METHODS.some(
            (item) => item.value === paymentMethod
        )
    ) {
        errors.payment_method = "Select a payment method.";
    }

    if (paymentMethod === "BANK") {
        if (!BANKS.some((item) => item.value === bankName)) {
            errors.bank_name = "Select the bank used for payment.";
        }
    } else if (bankName) {
        errors.bank_name =
            "Bank selection is only allowed for bank payments.";
    }

    return errors;
}

function StatCard({
    title,
    value,
    description,
    icon: Icon,
    variant = "navy",
}) {
    return (
        <div className={`oe-stat-card oe-stat-${variant}`}>
            <div className="oe-stat-top">
                <span className="oe-stat-title">{title}</span>

                <span className="oe-stat-icon">
                    <Icon size={20} strokeWidth={2} />
                </span>
            </div>

            <div className="oe-stat-value">{value}</div>

            <div className="oe-stat-description">
                {description}
            </div>
        </div>
    );
}

function Field({
    label,
    required = false,
    error,
    hint,
    children,
}) {
    return (
        <div className="oe-field">
            <label className="oe-label">
                {label}

                {required && (
                    <span className="oe-required"> *</span>
                )}
            </label>

            {children}

            {hint && !error && (
                <span className="oe-field-hint">{hint}</span>
            )}

            {error && (
                <span className="oe-field-error">{error}</span>
            )}
        </div>
    );
}

function ApplicantFormModal({
    open,
    applicant,
    saving,
    onClose,
    onSubmit,
}) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});

    const editing = Boolean(applicant?.id);

    useEffect(() => {
        if (!open) return;

        setForm(
            applicant
                ? {
                      full_name: applicant.full_name ?? "",
                      status: applicant.status ?? "FAILED",
                      amount_paid: String(
                          applicant.amount_paid ?? "0.00"
                      ),
                      payment_method:
                          applicant.payment_method ?? "",
                      bank_name: applicant.bank_name ?? "",
                  }
                : { ...EMPTY_FORM }
        );

        setErrors({});
    }, [open, applicant]);

    if (!open) return null;

    const updateField = (name, value) => {
        setForm((previous) => {
            const next = {
                ...previous,
                [name]: value,
            };

            if (name === "payment_method" && value !== "BANK") {
                next.bank_name = "";
            }

            return next;
        });

        setErrors((previous) => ({
            ...previous,
            [name]: undefined,
            ...(name === "payment_method"
                ? { bank_name: undefined }
                : {}),
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const nextErrors = validateForm(form);

        setErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            await showAlert({
                icon: "warning",
                title: "Check the form",
                text: "Please correct the highlighted fields before saving.",
            });

            return;
        }

        await onSubmit({
            full_name: normalizeText(form.full_name),
            status: form.status,
            amount_paid: Number(form.amount_paid).toFixed(2),
            payment_method: form.payment_method,
            bank_name:
                form.payment_method === "BANK"
                    ? form.bank_name
                    : "",
        });
    };

    return (
        <div
            className="oe-modal-backdrop"
            onMouseDown={(event) => {
                if (
                    event.target === event.currentTarget &&
                    !saving
                ) {
                    onClose();
                }
            }}
        >
            <section
                className="oe-modal oe-form-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="oe-form-title"
            >
                <div className="oe-modal-header">
                    <div className="oe-modal-heading">
                        <div className="oe-modal-icon">
                            {editing ? (
                                <Pencil size={21} />
                            ) : (
                                <FilePlus2 size={22} />
                            )}
                        </div>

                        <div>
                            <h2 id="oe-form-title">
                                {editing
                                    ? "Edit Applicant"
                                    : "Register Applicant"}
                            </h2>

                            <p>
                                {editing
                                    ? "Update the applicant's record."
                                    : "Enter the offline entrance applicant's details."}
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="oe-icon-button"
                        onClick={onClose}
                        disabled={saving}
                        aria-label="Close form"
                    >
                        <X size={20} />
                    </button>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="oe-modal-body"
                    noValidate
                >
                    <div className="oe-form-section-title">
                        <UserRound size={17} />

                        <span>Applicant Information</span>
                    </div>

                    <Field
                        label="Full Name"
                        required
                        error={errors.full_name}
                    >
                        <input
                            className={`oe-input ${
                                errors.full_name ? "oe-input-error" : ""
                            }`}
                            type="text"
                            name="full_name"
                            value={form.full_name}
                            onChange={(event) =>
                                updateField(
                                    "full_name",
                                    event.target.value
                                )
                            }
                            placeholder="Enter applicant's full name"
                            maxLength={255}
                            autoComplete="name"
                            required
                        />
                    </Field>

                    <div className="oe-form-grid">
                        <Field
                            label="Examination Result"
                            required
                            error={errors.status}
                        >
                            <select
                                className="oe-input"
                                value={form.status}
                                onChange={(event) =>
                                    updateField(
                                        "status",
                                        event.target.value
                                    )
                                }
                                required
                            >
                                {RESULT_STATUSES.map((item) => (
                                    <option
                                        key={item.value}
                                        value={item.value}
                                    >
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        <Field
                            label="Amount Paid (LRD)"
                            required
                            error={errors.amount_paid}
                        >
                            <div className="oe-input-with-icon">
                                <span className="oe-input-prefix">
                                    LRD
                                </span>

                                <input
                                    className="oe-input oe-amount-input"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    inputMode="decimal"
                                    value={form.amount_paid}
                                    onChange={(event) =>
                                        updateField(
                                            "amount_paid",
                                            event.target.value
                                        )
                                    }
                                    placeholder="0.00"
                                    required
                                />
                            </div>
                        </Field>
                    </div>

                    <div className="oe-form-section-title oe-payment-heading">
                        <Wallet size={17} />

                        <span>Payment Information</span>
                    </div>

                    <Field
                        label="Payment Method"
                        required
                        error={errors.payment_method}
                    >
                        <select
                            className="oe-input"
                            value={form.payment_method}
                            onChange={(event) =>
                                updateField(
                                    "payment_method",
                                    event.target.value
                                )
                            }
                            required
                        >
                            <option value="" disabled>
                                Select payment method
                            </option>

                            {PAYMENT_METHODS.map((item) => (
                                <option
                                    key={item.value}
                                    value={item.value}
                                >
                                    {item.label}
                                </option>
                            ))}
                        </select>
                    </Field>

                    {form.payment_method === "BANK" && (
                        <Field
                            label="Bank Name"
                            required
                            error={errors.bank_name}
                        >
                            <select
                                className="oe-input"
                                value={form.bank_name}
                                onChange={(event) =>
                                    updateField(
                                        "bank_name",
                                        event.target.value
                                    )
                                }
                                required
                            >
                                <option value="" disabled>
                                    Select bank
                                </option>

                                {BANKS.map((bank) => (
                                    <option
                                        key={bank.value}
                                        value={bank.value}
                                    >
                                        {bank.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    )}

                    <div className="oe-form-note">
                        <ShieldCheck size={17} />

                        <span>
                            {editing
                                ? "Changes will be submitted to the applicant API."
                                : "The applicant record will be saved to the server after submission."}
                        </span>
                    </div>

                    <div className="oe-modal-footer">
                        <button
                            type="button"
                            className="oe-btn oe-btn-secondary"
                            onClick={onClose}
                            disabled={saving}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="oe-btn oe-btn-primary"
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <LoaderCircle
                                        size={17}
                                        className="oe-spin"
                                    />

                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Check size={18} />

                                    {editing
                                        ? "Save Changes"
                                        : "Register Applicant"}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
}

function ApplicantDetailsModal({ applicant, onClose, onEdit }) {
    if (!applicant) return null;

    return (
        <div
            className="oe-modal-backdrop"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <section
                className="oe-modal oe-details-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="oe-details-title"
            >
                <div className="oe-modal-header">
                    <div className="oe-modal-heading">
                        <div className="oe-modal-icon">
                            <ClipboardList size={22} />
                        </div>

                        <div>
                            <h2 id="oe-details-title">
                                Applicant Details
                            </h2>

                            <p>
                                Record #{applicant.id}
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="oe-icon-button"
                        onClick={onClose}
                        aria-label="Close details"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="oe-details-content">
                    <div className="oe-profile-summary">
                        <div className="oe-profile-avatar">
                            <UserRound size={30} />
                        </div>

                        <div className="oe-profile-name">
                            <h3>{applicant.full_name}</h3>

                            <span>
                                Offline Entrance Applicant
                            </span>
                        </div>

                        <span
                            className={`oe-status ${
                                applicant.status === "PASSED"
                                    ? "oe-status-passed"
                                    : "oe-status-failed"
                            }`}
                        >
                            {applicant.status === "PASSED" ? (
                                <CheckCircle2 size={14} />
                            ) : (
                                <XCircle size={14} />
                            )}

                            {applicant.status}
                        </span>
                    </div>

                    <div className="oe-detail-grid">
                        <div className="oe-detail-item">
                            <span className="oe-detail-label">
                                Applicant ID
                            </span>

                            <strong>#{applicant.id}</strong>
                        </div>

                        <div className="oe-detail-item">
                            <span className="oe-detail-label">
                                Amount Paid
                            </span>

                            <strong className="oe-detail-amount">
                                {formatMoney(applicant.amount_paid)}
                            </strong>
                        </div>

                        <div className="oe-detail-item oe-detail-full">
                            <span className="oe-detail-label">
                                Payment Method
                            </span>

                            <strong>
                                {getPaymentLabel(
                                    applicant.payment_method
                                )}
                            </strong>
                        </div>

                        {applicant.payment_method === "BANK" && (
                            <div className="oe-detail-item oe-detail-full">
                                <span className="oe-detail-label">
                                    Bank Name
                                </span>

                                <strong>
                                    {getBankLabel(applicant.bank_name)}
                                </strong>
                            </div>
                        )}

                        <div className="oe-detail-item oe-detail-full">
                            <span className="oe-detail-label">
                                Date Registered
                            </span>

                            <strong>
                                {formatDate(applicant.created_at)}
                            </strong>
                        </div>
                    </div>

                    <div className="oe-modal-footer">
                        <button
                            type="button"
                            className="oe-btn oe-btn-secondary"
                            onClick={onClose}
                        >
                            Close
                        </button>

                        <button
                            type="button"
                            className="oe-btn oe-btn-primary"
                            onClick={() => onEdit(applicant)}
                        >
                            <Pencil size={17} />

                            Edit Applicant
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Pagination({
    page,
    pageSize,
    count,
    totalPages,
    hasNext,
    hasPrevious,
    onPageChange,
    onPageSizeChange,
}) {
    const start = count === 0 ? 0 : (page - 1) * pageSize + 1;

    const end = Math.min(page * pageSize, count);

    const pages = useMemo(() => {
        const result = new Set([
            1,
            totalPages,
            page - 1,
            page,
            page + 1,
        ]);

        return [...result]
            .filter((number) => number >= 1 && number <= totalPages)
            .sort((a, b) => a - b);
    }, [page, totalPages]);

    return (
        <div className="oe-pagination">
            <div className="oe-pagination-info">
                <span>
                    Showing <strong>{start}–{end}</strong> of{" "}
                    <strong>{count.toLocaleString()}</strong> applicants
                </span>

                <label className="oe-page-size">
                    <span>Rows</span>

                    <select
                        value={pageSize}
                        onChange={(event) =>
                            onPageSizeChange(Number(event.target.value))
                        }
                        aria-label="Rows per page"
                    >
                        {PAGE_SIZES.map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <nav
                className="oe-page-controls"
                aria-label="Applicant pagination"
            >
                <button
                    type="button"
                    className="oe-page-arrow"
                    disabled={!hasPrevious || page <= 1}
                    onClick={() => onPageChange(page - 1)}
                    aria-label="Previous page"
                >
                    <ChevronLeft size={18} />
                </button>

                {pages.map((number, index) => {
                    const previousNumber = pages[index - 1];

                    const hasGap =
                        previousNumber &&
                        number - previousNumber > 1;

                    return (
                        <React.Fragment key={number}>
                            {hasGap && (
                                <span className="oe-page-ellipsis">
                                    …
                                </span>
                            )}

                            <button
                                type="button"
                                className={`oe-page-number ${
                                    number === page
                                        ? "oe-page-active"
                                        : ""
                                }`}
                                aria-current={
                                    number === page ? "page" : undefined
                                }
                                onClick={() => onPageChange(number)}
                            >
                                {number}
                            </button>
                        </React.Fragment>
                    );
                })}

                <button
                    type="button"
                    className="oe-page-arrow"
                    disabled={!hasNext || page >= totalPages}
                    onClick={() => onPageChange(page + 1)}
                    aria-label="Next page"
                >
                    <ChevronRight size={18} />
                </button>
            </nav>
        </div>
    );
}

export default function EntranceOfflineEntranceApplicants () {
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [count, setCount] = useState(0);
    const [nextUrl, setNextUrl] = useState(null);
    const [previousUrl, setPreviousUrl] = useState(null);

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [paymentFilter, setPaymentFilter] = useState("");
    const [ordering, setOrdering] = useState("-created_at");

    const [formOpen, setFormOpen] = useState(false);
    const [editingApplicant, setEditingApplicant] = useState(null);
    const [selectedApplicant, setSelectedApplicant] = useState(null);

    const requestIdRef = useRef(0);
    const searchTimerRef = useRef(null);
    const mountedRef = useRef(true);

    const totalPages = Math.max(1, Math.ceil(count / pageSize));

    const hasNext = Boolean(nextUrl) || page < totalPages;

    const hasPrevious = Boolean(previousUrl) || page > 1;

    const loadApplicants = useCallback(
        async ({ showRefresh = false } = {}) => {
            const requestId = ++requestIdRef.current;

            if (showRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            try {
                const params = {
                    page,
                    page_size: pageSize,
                    ordering,
                };

                if (search) {
                    params.search = search;
                }

                /*
                 * The current backend does not implement custom
                 * status/payment filters. We request one page from
                 * the server and filter only that returned page.
                 *
                 * For genuinely server-wide filtering, add
                 * django-filter FilterSet support to the backend.
                 */
                const response = await api.get(ENDPOINT, {
                    params,
                });

                if (
                    !mountedRef.current ||
                    requestId !== requestIdRef.current
                ) {
                    return;
                }

                const result = getPageData(response);

                setApplicants(result.results);
                setCount(result.count);
                setNextUrl(result.next);
                setPreviousUrl(result.previous);
            } catch (error) {
                if (
                    !mountedRef.current ||
                    requestId !== requestIdRef.current
                ) {
                    return;
                }

                setApplicants([]);
                setCount(0);
                setNextUrl(null);
                setPreviousUrl(null);

                await showAlert({
                    icon: "error",
                    title: "Unable to load applicants",
                    text: getErrorMessage(error),
                });
            } finally {
                if (
                    mountedRef.current &&
                    requestId === requestIdRef.current
                ) {
                    setLoading(false);
                    setRefreshing(false);
                }
            }
        },
        [page, pageSize, search, ordering]
    );

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            requestIdRef.current += 1;

            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        loadApplicants();
    }, [loadApplicants]);

    useEffect(() => {
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }

        searchTimerRef.current = setTimeout(() => {
            setPage(1);
            setSearch(normalizeText(searchInput));
        }, 350);

        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
            }
        };
    }, [searchInput]);

    /*
     * Local filtering is limited to the current server page.
     * Do not interpret these counts as system-wide statistics.
     */
    const filteredApplicants = useMemo(() => {
        return applicants.filter((applicant) => {
            const statusMatches =
                !statusFilter || applicant.status === statusFilter;

            const paymentMatches =
                !paymentFilter ||
                applicant.payment_method === paymentFilter;

            return statusMatches && paymentMatches;
        });
    }, [applicants, statusFilter, paymentFilter]);

    const pageStats = useMemo(() => {
        const passed = applicants.filter(
            (item) => item.status === "PASSED"
        ).length;

        const failed = applicants.filter(
            (item) => item.status === "FAILED"
        ).length;

        const collected = applicants.reduce(
            (sum, item) => sum + (Number(item.amount_paid) || 0),
            0
        );

        return {
            loaded: applicants.length,
            passed,
            failed,
            collected,
        };
    }, [applicants]);

    const openCreateForm = () => {
        setEditingApplicant(null);
        setFormOpen(true);
    };

    const openEditForm = (applicant) => {
        setSelectedApplicant(null);
        setEditingApplicant(applicant);
        setFormOpen(true);
    };

    const handleSave = async (payload) => {
        setSaving(true);

        try {
            if (editingApplicant?.id) {
                /*
                 * The supplied ViewSet does not currently support
                 * PUT or PATCH. This request will work after you
                 * add UpdateModelMixin or enable updates in the API.
                 */
                await api.patch(
                    `${ENDPOINT}${editingApplicant.id}/`,
                    payload
                );

                setFormOpen(false);

                await showAlert({
                    icon: "success",
                    title: "Applicant updated",
                    text: "The applicant record was updated successfully.",
                });
            } else {
                await api.post(ENDPOINT, payload);

                setFormOpen(false);

                await showAlert({
                    icon: "success",
                    title: "Registration successful",
                    text: "The offline entrance applicant has been registered.",
                });
            }

            await loadApplicants({ showRefresh: true });
        } catch (error) {
            await showAlert({
                icon: "error",
                title: editingApplicant
                    ? "Update unsuccessful"
                    : "Registration unsuccessful",
                text: getErrorMessage(error),
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (applicant) => {
        const result = await confirmAction({
            title: "Delete applicant?",
            text: `This will attempt to permanently delete ${applicant.full_name}'s record.`,
            confirmButtonText: "Delete Applicant",
        });

        if (!result.isConfirmed) return;

        try {
            /*
             * DELETE is not currently enabled in the supplied
             * ViewSet. Enable DestroyModelMixin first.
             */
            await api.delete(`${ENDPOINT}${applicant.id}/`);

            await showAlert({
                icon: "success",
                title: "Applicant deleted",
                text: "The record was deleted successfully.",
            });

            if (applicants.length === 1 && page > 1) {
                setPage((current) => current - 1);
            } else {
                await loadApplicants({ showRefresh: true });
            }
        } catch (error) {
            await showAlert({
                icon: "error",
                title: "Unable to delete applicant",
                text: getErrorMessage(error),
            });
        }
    };

    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setPage(1);
    };

    const handleRefresh = () => {
        loadApplicants({ showRefresh: true });
    };

    const toggleOrdering = (field) => {
        setOrdering((current) =>
            current === field ? `-${field}` : field
        );

        setPage(1);
    };

    const clearFilters = () => {
        setSearchInput("");
        setSearch("");
        setStatusFilter("");
        setPaymentFilter("");
        setPage(1);
    };

    const exportCurrentPage = () => {
        if (!filteredApplicants.length) {
            showAlert({
                icon: "info",
                title: "Nothing to export",
                text: "There are no applicants on this page matching the selected filters.",
            });

            return;
        }

        const columns = [
            "ID",
            "Full Name",
            "Result",
            "Amount Paid (LRD)",
            "Payment Method",
            "Bank Name",
            "Created At",
        ];

        const escapeCsv = (value) =>
            `"${String(value ?? "").replaceAll('"', '""')}"`;

        const rows = filteredApplicants.map((item) => [
            item.id,
            item.full_name,
            item.status,
            item.amount_paid,
            getPaymentLabel(item.payment_method),
            item.payment_method === "BANK"
                ? getBankLabel(item.bank_name)
                : "",
            item.created_at,
        ]);

        const csv = [
            columns,
            ...rows,
        ]
            .map((row) => row.map(escapeCsv).join(","))
            .join("\r\n");

        const blob = new Blob(["\uFEFF", csv], {
            type: "text/csv;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `offline-entrance-applicants-page-${page}.csv`;

        document.body.appendChild(link);
        link.click();
        link.remove();

        URL.revokeObjectURL(url);
    };

    return (
        <div className="oe-page">
            <div className="oe-container">
                <header className="oe-page-header">
                    <div className="oe-page-title-area">
                        <div className="oe-title-icon">
                            <GraduationCap size={27} />
                        </div>

                        <div>
                            <div className="oe-breadcrumb">
                                <span>Admissions</span>

                                <ChevronRight size={14} />

                                <span>Entrance Examination</span>
                            </div>

                            <h1>Offline Entrance Applicants</h1>

                            <p>
                                Manage applicant registrations, entrance
                                results, and payment records.
                            </p>
                        </div>
                    </div>

                    <div className="oe-header-actions">
                        <button
                            type="button"
                            className="oe-btn oe-btn-secondary"
                            onClick={handleRefresh}
                            disabled={loading || refreshing}
                        >
                            <RefreshCw
                                size={17}
                                className={
                                    refreshing ? "oe-spin" : ""
                                }
                            />

                            Refresh
                        </button>

                        <button
                            type="button"
                            className="oe-btn oe-btn-primary"
                            onClick={openCreateForm}
                        >
                            <FilePlus2 size={18} />

                            Register Applicant
                        </button>
                    </div>
                </header>

                <section className="oe-stat-grid">
                    <StatCard
                        title="Total Applicants"
                        value={count.toLocaleString()}
                        description="Matching server-side search"
                        icon={Users}
                        variant="navy"
                    />

                    <StatCard
                        title="Passed"
                        value={pageStats.passed.toLocaleString()}
                        description={`On this page · ${pageStats.loaded} loaded`}
                        icon={CheckCircle2}
                        variant="green"
                    />

                    <StatCard
                        title="Failed"
                        value={pageStats.failed.toLocaleString()}
                        description={`On this page · ${pageStats.loaded} loaded`}
                        icon={XCircle}
                        variant="red"
                    />

                    <StatCard
                        title="Amount Collected"
                        value={formatMoney(pageStats.collected)}
                        description="Sum of the currently loaded page"
                        icon={CircleDollarSign}
                        variant="gold"
                    />
                </section>

                <section className="oe-main-card">
                    <div className="oe-card-heading">
                        <div>
                            <h2>Applicant Records</h2>

                            <p>
                                Search, filter, review, and manage
                                offline entrance registrations.
                            </p>
                        </div>

                        <button
                            type="button"
                            className="oe-btn oe-btn-outline"
                            onClick={exportCurrentPage}
                            disabled={loading || filteredApplicants.length === 0}
                        >
                            <Download size={17} />

                            Export Current Page
                        </button>
                    </div>

                    <div className="oe-toolbar">
                        <div className="oe-search-wrap">
                            <Search
                                size={19}
                                className="oe-search-icon"
                            />

                            <input
                                type="search"
                                className="oe-search-input"
                                placeholder="Search applicant name..."
                                value={searchInput}
                                onChange={(event) =>
                                    setSearchInput(event.target.value)
                                }
                                aria-label="Search applicant names"
                            />

                            {searchInput && (
                                <button
                                    type="button"
                                    className="oe-search-clear"
                                    onClick={() => setSearchInput("")}
                                    aria-label="Clear search"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        <div className="oe-filter-control">
                            <Filter size={17} />

                            <select
                                value={statusFilter}
                                onChange={(event) =>
                                    setStatusFilter(event.target.value)
                                }
                                aria-label="Filter by result"
                            >
                                <option value="">All Results</option>

                                {RESULT_STATUSES.map((item) => (
                                    <option
                                        key={item.value}
                                        value={item.value}
                                    >
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="oe-filter-control">
                            <Wallet size={17} />

                            <select
                                value={paymentFilter}
                                onChange={(event) =>
                                    setPaymentFilter(event.target.value)
                                }
                                aria-label="Filter by payment method"
                            >
                                <option value="">All Payments</option>

                                {PAYMENT_METHODS.map((item) => (
                                    <option
                                        key={item.value}
                                        value={item.value}
                                    >
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button
                            type="button"
                            className="oe-btn oe-btn-clear"
                            onClick={clearFilters}
                            disabled={
                                !searchInput &&
                                !statusFilter &&
                                !paymentFilter
                            }
                        >
                            <RefreshCw size={15} />

                            Clear
                        </button>
                    </div>

                    {(statusFilter || paymentFilter) && (
                        <div className="oe-filter-notice">
                            <Activity size={15} />

                            <span>
                                Result and payment filters apply to the
                                currently loaded server page. Search and
                                pagination are handled by the server.
                            </span>
                        </div>
                    )}

                    <div className="oe-table-meta">
                        <span>
                            <span className="oe-live-dot" />

                            {loading
                                ? "Loading records..."
                                : `${filteredApplicants.length} records displayed on this page`}
                        </span>

                        <span className="oe-table-meta-right">
                            <Clock3 size={14} />

                            Ordered by newest first unless changed
                        </span>
                    </div>

                    <div className="oe-table-scroll">
                        <table className="oe-table">
                            <thead>
                                <tr>
                                    <th className="oe-th-id">ID</th>

                                    <th>
                                        <button
                                            type="button"
                                            className="oe-sort-button"
                                            onClick={() =>
                                                toggleOrdering("full_name")
                                            }
                                        >
                                            Applicant

                                            {ordering.replace("-", "") ===
                                            "full_name" ? (
                                                ordering.startsWith("-") ? (
                                                    <ArrowDown size={14} />
                                                ) : (
                                                    <ArrowDownUp size={14} />
                                                )
                                            ) : (
                                                <ArrowDownUp size={14} />
                                            )}
                                        </button>
                                    </th>

                                    <th>
                                        <button
                                            type="button"
                                            className="oe-sort-button"
                                            onClick={() =>
                                                toggleOrdering("status")
                                            }
                                        >
                                            Result

                                            <ArrowDownUp size={14} />
                                        </button>
                                    </th>

                                    <th>
                                        <button
                                            type="button"
                                            className="oe-sort-button"
                                            onClick={() =>
                                                toggleOrdering("amount_paid")
                                            }
                                        >
                                            Amount Paid

                                            <ArrowDownUp size={14} />
                                        </button>
                                    </th>

                                    <th>Payment Method</th>

                                    <th>
                                        <button
                                            type="button"
                                            className="oe-sort-button"
                                            onClick={() =>
                                                toggleOrdering("created_at")
                                            }
                                        >
                                            Date Registered

                                            <ArrowDownUp size={14} />
                                        </button>
                                    </th>

                                    <th className="oe-th-actions">
                                        Actions
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="oe-table-state"
                                        >
                                            <LoaderCircle
                                                size={30}
                                                className="oe-spin oe-loading-icon"
                                            />

                                            <strong>
                                                Loading applicants
                                            </strong>

                                            <span>
                                                Retrieving records from
                                                the server...
                                            </span>
                                        </td>
                                    </tr>
                                ) : filteredApplicants.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="oe-table-state"
                                        >
                                            <div className="oe-empty-icon">
                                                <Users size={29} />
                                            </div>

                                            <strong>
                                                {applicants.length
                                                    ? "No matching records"
                                                    : "No applicants found"}
                                            </strong>

                                            <span>
                                                {search ||
                                                statusFilter ||
                                                paymentFilter
                                                    ? "Try changing your search or filters."
                                                    : "Register an applicant to get started."}
                                            </span>

                                            {!search &&
                                                !statusFilter &&
                                                !paymentFilter && (
                                                    <button
                                                        type="button"
                                                        className="oe-btn oe-btn-primary"
                                                        onClick={
                                                            openCreateForm
                                                        }
                                                    >
                                                        <FilePlus2 size={17} />

                                                        Register Applicant
                                                    </button>
                                                )}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredApplicants.map((applicant) => (
                                        <tr key={applicant.id}>
                                            <td className="oe-id-cell">
                                                <span>
                                                    #{applicant.id}
                                                </span>
                                            </td>

                                            <td>
                                                <div className="oe-applicant-cell">
                                                    <div className="oe-avatar">
                                                        {normalizeText(
                                                            applicant.full_name
                                                        )
                                                            .split(/\s+/)
                                                            .slice(0, 2)
                                                            .map((part) =>
                                                                part
                                                                    .charAt(0)
                                                                    .toUpperCase()
                                                            )
                                                            .join("")}
                                                    </div>

                                                    <div className="oe-applicant-info">
                                                        <strong>
                                                            {applicant.full_name}
                                                        </strong>

                                                        <span>
                                                            Offline applicant
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <span
                                                    className={`oe-status ${
                                                        applicant.status ===
                                                        "PASSED"
                                                            ? "oe-status-passed"
                                                            : "oe-status-failed"
                                                    }`}
                                                >
                                                    {applicant.status ===
                                                    "PASSED" ? (
                                                        <CheckCircle2
                                                            size={14}
                                                        />
                                                    ) : (
                                                        <XCircle size={14} />
                                                    )}

                                                    {applicant.status}
                                                </span>
                                            </td>

                                            <td>
                                                <strong className="oe-money-cell">
                                                    {formatMoney(
                                                        applicant.amount_paid
                                                    )}
                                                </strong>
                                            </td>

                                            <td>
                                                <div className="oe-payment-cell">
                                                    {applicant.payment_method ===
                                                    "BANK" ? (
                                                        <Building2 size={16} />
                                                    ) : applicant.payment_method ===
                                                      "CASH" ? (
                                                        <Banknote size={16} />
                                                    ) : (
                                                        <CreditCard size={16} />
                                                    )}

                                                    <span>
                                                        {getPaymentLabel(
                                                            applicant.payment_method
                                                        )}
                                                    </span>
                                                </div>

                                                {applicant.payment_method ===
                                                    "BANK" &&
                                                    applicant.bank_name && (
                                                        <div className="oe-bank-subtitle">
                                                            {getBankLabel(
                                                                applicant.bank_name
                                                            )}
                                                        </div>
                                                    )}
                                            </td>

                                            <td>
                                                <div className="oe-date-cell">
                                                    <CalendarDays
                                                        size={15}
                                                    />

                                                    <span>
                                                        {formatDate(
                                                            applicant.created_at
                                                        )}
                                                    </span>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="oe-row-actions">
                                                    <button
                                                        type="button"
                                                        className="oe-action-btn oe-action-view"
                                                        title="View details"
                                                        aria-label={`View ${applicant.full_name}`}
                                                        onClick={() =>
                                                            setSelectedApplicant(
                                                                applicant
                                                            )
                                                        }
                                                    >
                                                        <Eye size={17} />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="oe-action-btn oe-action-edit"
                                                        title="Edit applicant"
                                                        aria-label={`Edit ${applicant.full_name}`}
                                                        onClick={() =>
                                                            openEditForm(
                                                                applicant
                                                            )
                                                        }
                                                    >
                                                        <Pencil size={16} />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="oe-action-btn oe-action-delete"
                                                        title="Delete applicant"
                                                        aria-label={`Delete ${applicant.full_name}`}
                                                        onClick={() =>
                                                            handleDelete(
                                                                applicant
                                                            )
                                                        }
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {!loading && count > 0 && (
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            count={count}
                            totalPages={totalPages}
                            hasNext={hasNext}
                            hasPrevious={hasPrevious}
                            onPageChange={setPage}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    )}
                </section>

                <footer className="oe-page-footer">
                    <span>
                        <ShieldCheck size={15} />

                        Offline Entrance Applicant Management
                    </span>

                    <span>
                        Design by Polu Integrated School Management System
                    </span>
                </footer>
            </div>

            <ApplicantFormModal
                open={formOpen}
                applicant={editingApplicant}
                saving={saving}
                onClose={() => {
                    if (!saving) {
                        setFormOpen(false);
                        setEditingApplicant(null);
                    }
                }}
                onSubmit={handleSave}
            />

            <ApplicantDetailsModal
                applicant={selectedApplicant}
                onClose={() => setSelectedApplicant(null)}
                onEdit={openEditForm}
            />
        </div>
    );
}
